// Deterministic, local "Jarvis" text generation for the FF01 prototype.
// No live model is called — every string here is a pure function of demo
// state so the prototype is repeatable. Jarvis output is ALWAYS rendered in
// the violet AI semantic family and is never presented as verified fact.
// See docs/ff01/FF01_PRODUCT_MODEL.md "Truth vs. interpretation".
import type { Situation } from "./types";

export function rankingRationale(top: Situation, runnerUp: Situation | null): string {
  if (top.id === "sit-hartley") {
    return "Hartley leads today: the late balance and the renewal window compound, and the open compressor work order is likely connected to the payment slip. One conversation can address the balance, confirm the repair, and open the renewal — before your 10:00 tour.";
  }
  if (runnerUp) {
    return `${top.title} ranks first on combined financial, operational, and time-sensitivity signals${runnerUp ? `, ahead of ${runnerUp.title}` : ""}.`;
  }
  return `${top.title} is the highest-leverage item open right now.`;
}

export function assembledBrief(situation: Situation): string {
  switch (situation.id) {
    case "sit-hartley":
      return "Payment slipped after July's compressor fault — the open work order and the late balance are likely connected. Renewal opens Oct 11. Recommend one call: confirm the repair resolved it, take payment, then open the renewal conversation.";
    case "sit-wo-1163":
      return "Meridian marked the dock-door repair complete Friday. No verification recorded yet — a reinspection is queued for your 1:30 vendor window today.";
    case "sit-wo-1201":
      return "Storefront glass is on order for the Avon 212 turnover; Summit Glass has this scheduled for install this week, in line with the target turnover date.";
    case "sit-tour-brightline":
      return "Brightline is a warm HubSpot lead wanting fast move-in. Unit 8014 fits their budget and use case — lead with flexible lease terms and confirm the move-in timeline early.";
    case "sit-tour-avon":
      return "Heartland Fixtures is a newer inbound from Google Calendar — lighter context than a HubSpot lead. Confirm use case and budget fit during prep.";
    case "sit-movein-8022":
      return "Summit Signworks is on track for Sep 8. Two readiness items remain: exterior photos and access-fob issuance. Both are quick to close before the walkthrough.";
    case "sit-coi-8110":
      return "Vantage 3PL's certificate of insurance expires Sep 30. A reminder is drafted but not yet sent — this is a small, high-leverage compliance item to close now.";
    case "sit-moveout-avon-212":
      return "Turnover on Avon 212 is in the actions phase — storefront glass is the last open item before the unit is ready to show.";
    default:
      return `${situation.title}: assembled from ${situation.sources.length} verified source${situation.sources.length === 1 ? "" : "s"}. Review the facts below before acting.`;
  }
}

export function draftMessage(situation: Situation): string {
  switch (situation.id) {
    case "sit-hartley":
      return "Hi Dana — following up on the compressor repair Meridian completed on the 22nd. Can you confirm it's holding steady? Also wanted to touch base on the account balance ($2,450, opened 12 days ago) and let you know your renewal window opens October 11 — happy to talk through options whenever works for you.";
    case "sit-coi-8110":
      return "Hi Marcus — your certificate of insurance for Unit 8110 is set to expire September 30. Could you have your carrier send an updated COI when you get a chance? Happy to help if you need the requirements resent.";
    default:
      return `Hi — following up on ${situation.title.toLowerCase()}. Let me know if you have a few minutes to connect.`;
  }
}

export function suggestedActions(situation: Situation): string[] {
  switch (situation.id) {
    case "sit-hartley":
      return ["Call Dana Hartley to confirm the repair held and discuss the balance.", "Send the drafted renewal-window message once the call is complete.", "Verify the compressor work order during your next site walk."];
    case "sit-wo-1163":
      return ["Verify the dock-door repair during your 1:30 vendor window.", "Close the work order once verification passes."];
    default:
      return [`Review ${situation.title} and decide next step.`];
  }
}

export function daySummary(needsYouCount: number, scheduledCount: number, topTitle: string): string {
  return `${needsYouCount} situation${needsYouCount === 1 ? "" : "s"} need you today, ${scheduledCount} scheduled event${scheduledCount === 1 ? "" : "s"}. Start with ${topTitle} — it carries the most combined risk.`;
}

export function inspectionSuggestedAction(itemTitle: string, category: string, result: "attention" | "fail"): { title: string; description: string } {
  if (result === "fail") {
    return {
      title: `Urgent repair — ${itemTitle}`,
      description: `Flagged FAIL during today's walk (${category}). Recommend dispatching a vendor before next visit.`
    };
  }
  return {
    title: `Monitor — ${itemTitle}`,
    description: `Flagged ATTENTION during today's walk (${category}). Recommend a follow-up check on the next visit; attach the photo captured today for comparison.`
  };
}
